import { useContext, useState } from 'react';
import {
  Box,
  Button,
  Collapsible,
  Data,
  DataFilter,
  DataFilters,
  Grid,
  ResponsiveContext,
  ThemeContext,
  TextInput,
  Text,
} from 'grommet';
import { Down, LinkNext, Close, Erase } from '@hpe-design/icons-grommet';
import { ListResults } from '../ListResults';
import { getGlassStyle } from '../../../../helpers/GlassStyles';

const allReferences = require('../../../../data/smart-search/references.json');

const SIDEBAR_WIDTHS = {
  medium: 'small',
  large: '20%',
  xlarge: '25%',
};

const pageContentGrid = {
  columns: {
    xsmall: 'auto',
    small: 'auto',
    medium: [SIDEBAR_WIDTHS.medium, 'flex'],
    large: [SIDEBAR_WIDTHS.large, 'flex'],
    xlarge: [SIDEBAR_WIDTHS.xlarge, 'flex'],
  },
  gap: 'large',
};

const uniqueWorkloads = [...new Set(allReferences.flatMap(ref => ref.workloads || []))];

const properties = {
  workloads: { label: 'Workloads', options: uniqueWorkloads },
  tech: { label: 'Technology' },
  sustainability: { label: 'Sustainability' },
  security: { label: 'Security' },
  pricing: { label: 'Business Models' },
};

/* ---------------- FILTER COMPONENT ---------------- */

const Filter = ({ label, expand, setExpand, property, isDarkMode }) => {

  const glass = getGlassStyle(isDarkMode, 1.01);

  return (
    <Box direction="column">
      <Button plain hoverIndicator onClick={() => setExpand(!expand)}>
        <Box direction="row" align="center" gap="small" pad={{ vertical: 'xsmall', horizontal: 'small' }}>
          {expand ? <Down size="small" /> : <LinkNext size="small" />}
          <Text weight="bold" size="small">{label}</Text>
        </Box>
      </Button>

      <Collapsible open={expand}>
        <Box pad={{ left: 'medium', top: 'xsmall', bottom: 'small' }}>

          <DataFilter
            property={property}

            /* GLASS BUTTON CONTROL */
            control={(props) => (
              <Box
                {...props}
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                round="small"

                style={{
                  ...glass.container,
                  borderRadius: '10px',
                }}
              />
            )}

            /* GLASS DROPDOWN PANEL */
            dropProps={{
              elevation: 'none',
              margin: { top: 'xsmall' },
              background: 'transparent',
              style: {
                ...glass.container,
                borderRadius: '12px',
              },
            }}
          />
        </Box>
      </Collapsible>
    </Box>
  );
};

/* ---------------- MAIN PAGE ---------------- */

export const ReferencesComp = () => {
  const breakpoint = useContext(ResponsiveContext);
  const theme = useContext(ThemeContext);
  const isDarkMode = theme.dark;
  const isMobile = ['xsmall', 'small'].includes(breakpoint);

  const glass = getGlassStyle(isDarkMode);

  const [searchQuery, setSearchQuery] = useState('');

  const [expandCategory, setExpandCategory] = useState(false);
  const [expandTechnology, setExpandTechnology] = useState(false);
  const [expandPricing, setExpandPricing] = useState(false);
  const [expandSustainability, setExpandSustainability] = useState(false);
  const [expandSecurity, setExpandSecurity] = useState(false);

  const filteredReferences = allReferences.filter(reference => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    const keywords = query.split(/\s+/);

    return keywords.every(word =>
      reference.title?.toLowerCase().includes(word) ||
      reference.publisher?.toLowerCase().includes(word) ||
      reference.tech?.toLowerCase().includes(word) ||
      reference.workloads?.some(w => w.toLowerCase().includes(word))
    );
  });

  return (
    <Box pad={{ top: 'small' }} background="transparent">
      <Data data={filteredReferences} properties={properties}>

        {/* HEADER */}
        <Box direction="row" gap="large" height="48px" align="center" margin={{ bottom: 'xxsmall' }}>
          {!isMobile && <Box width={SIDEBAR_WIDTHS[breakpoint] || '20%'} />}

          <Box direction="row" align="center" justify="between" flex>
            <Box direction="row" align="center" gap="xsmall" pad={{ left: 'medium' }}>
              <Box background="brand" pad={{ horizontal: 'small' }} round="full">
                <Text size="medium" weight="bold" color="white">{filteredReferences.length}</Text>
              </Box>
              <Text size="medium" weight="bold">References Found</Text>
            </Box>

            {searchQuery && (
              <Box pad={{ right: 'medium' }}>
                <Button plain onClick={() => setSearchQuery('')}>
                  <Box direction="row" align="center" gap="xsmall">
                    <Erase size="medium" />
                    <Text size="medium" weight="bold">Clear All</Text>
                  </Box>
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* GRID */}
        <Grid columns={pageContentGrid.columns[breakpoint]} gap="large" align="start">

          {/* SIDEBAR */}
          {!isMobile ? (
            <Box
              pad="medium"
              style={glass.container}
              onMouseEnter={glass.hover}
              onMouseLeave={glass.leave}
            >

              {/* SEARCH BAR */}
              <Box
                direction="row"
                align="center"
                round="small"
                margin={{ bottom: 'medium' }}
                style={{
                  ...glass.container,
                  borderRadius: '12px',
                }}
              >
                <TextInput
                  plain
                  placeholder="Search references..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button plain icon={<Close size="small" />} onClick={() => setSearchQuery('')} />
                )}
              </Box>

              {/* FILTERS */}
              <Filter label="Workloads" expand={expandCategory} setExpand={setExpandCategory} property="workloads" isDarkMode={isDarkMode} />
              <Filter label="Technology" expand={expandTechnology} setExpand={setExpandTechnology} property="tech" isDarkMode={isDarkMode} />
              <Filter label="Sustainability" expand={expandSustainability} setExpand={setExpandSustainability} property="sustainability" isDarkMode={isDarkMode} />
              <Filter label="Security" expand={expandSecurity} setExpand={setExpandSecurity} property="security" isDarkMode={isDarkMode} />
              <Filter label="Business Models" expand={expandPricing} setExpand={setExpandPricing} property="pricing" isDarkMode={isDarkMode} />

            </Box>
          ) : (

            /* MOBILE FILTER SHEET */
            <DataFilters
              layer
              layerProps={{
                background: 'transparent',
                style: glass.container,
              }}
            />
          )}

          {/* RESULTS */}
          <Box>
            {filteredReferences.length > 0 ? (
              <ListResults />
            ) : (

              /* EMPTY STATE GLASS */
              <Box
                align="center"
                pad="xlarge"
                style={{
                  ...glass.container,
                  borderStyle: 'dashed',
                }}
              >
                <Text weight="bold" margin="medium">
                  No matches found for your search.
                </Text>

                <Button
                  label="Reset All"
                  onClick={() => setSearchQuery('')}
                  style={{
                    ...glass.container,
                    borderRadius: '8px',
                    padding: '6px 14px',
                  }}
                />
              </Box>
            )}
          </Box>

        </Grid>
      </Data>
    </Box>
  );
};

